<template>
    <loading-page-layout :loaded="loaded">
        <expired-offers-snackbar :is-offers-expired="isOffersExpired" @refresh="goToSearchPage" />
        <mobile-info-page-tabs v-if="$breakpoint.smAndDown" v-model="mobileTabs" />
        <v-container fluid>
            <transfer-breadcrumbs v-if="!$breakpoint.smAndDown" :search-request="searchRequest" :product="product" />
            <v-row>
                <v-col v-show="!$breakpoint.smAndDown || ($breakpoint.smAndDown && mobileTabs === 0)" md="8">
                    <h1 v-if="product.info" class="mb-1 text-h6">
                        {{ product.info.name }}
                        <v-chip
                            v-if="
                                product.offers &&
                                product.offers[0] &&
                                product.offers[0].transferType &&
                                product.offers[0].transferType !== 'UNKNOWN'
                            "
                            label
                            size="x-small"
                        >
                            {{ $t(`filters_transferType.${product.offers[0].transferType}`) }}
                        </v-chip>
                        <ProductVariantLabel :product="product" class="ms-1" />
                    </h1>
                    <supplier-name-label v-if="isTO1 && product.info" :supplier-name="product.info.supplierName" />
                    <transfer-operated-by-label :product="product" class="mb-2" />
                    <component
                        :is="!$breakpoint.smAndDown ? 'product-gallery' : 'product-gallery-mobile'"
                        :images-data="uniqueImages"
                    />
                    <template v-if="!$breakpoint.smAndDown">
                        <transfer-offer-card
                            v-for="(offer, offerIndex) in product.offers"
                            :key="offer.offerKey"
                            :offer="offer"
                            :data-cy="`transfer-offer-card-${offerIndex}`"
                            @images="images = [...images, ...$event]"
                        />
                    </template>
                    <transfer-info :product="product" />
                    <view-offers-button @show-offers="mobileTabs = 1" />
                </v-col>
                <v-col v-show="!$breakpoint.smAndDown || ($breakpoint.smAndDown && mobileTabs === 1)" md="4">
                    <transfer-search-summary :search-request="searchRequest" />
                    <template v-if="$breakpoint.smAndDown">
                        <transfer-offer-card
                            v-for="offer in product.offers"
                            :key="offer.offerKey"
                            :offer="offer"
                            @images="images = [...images, ...$event]"
                        />
                    </template>
                    <div style="position: relative">
                        <client-only>
                            <transfer-map
                                style="position: relative; max-height: 70vh; top: 0 !important"
                                :points="mapPoints"
                                class="disable-top"
                                :parent="true"
                            />
                        </client-only>
                    </div>
                    <!--transfer-allowed-points /-->
                </v-col>
            </v-row>
        </v-container>
        <conditions-modal>
            <template v-slot:info>
                <transfer-conditions-modal-info />
            </template>
        </conditions-modal>
        <transfer-info-modal />
    </loading-page-layout>
</template>

<script lang="ts">
    import {Component, mixins} from 'vue-facing-decorator'
    import MobileInfoPageTabs from '@/components/snippets/MobileInfoPageTabs.vue'
    import ConditionsModal from '~/components/modals/ConditionsModal.vue'
    import TransferBreadcrumbs from '@/components/transfers/TransferBreadcrumbs.vue'
    import TransferOfferCard from '~src/components/transfers/info/transferOfferCard.src'
    import TransferInfo from '@/components/transfers/info/TransferInfo.vue'
    import TransferSearchSummary from '@/components/transfers/search/TransferSearchSummary.vue'
    import TransferMap from '@/components/transfers/search/TransferMap.vue'
    import TransferInfoModal from '@/components/transfers/info/TransferInfoModal.vue'
    import {TransferStoreMixin} from '@/mixins/TransferStoreMixin.vue'
    import ViewOffersButton from '@/components/snippets/ViewOffersButton.vue'
    import ProductGallery from '@/components/info/ProductGallery.vue'
    import ProductGalleryMobile from '@/components/info/ProductGalleryMobile.vue'
    import {TransferPageMixin} from '@/components/transfers/mixins/TransferPageMixin.vue'
    import ExpiredOffersSnackbar from '@/components/search/ExpiredOffersSnackbar.vue'
    import TransferConditionsModalInfo from '@/components/transfers/snippets/TransferConditionsModalInfo.vue'
    import {SearchPageMixin} from '@/components/mixins/SearchPageMixin.vue'
    import {InfoPageMixin} from '@/components/mixins/InfoPageMixin.vue'
    import LoadingPageLayout from '@/components/LoadingPageLayout.vue'
    import {SearchPageAuthMixin} from '@/components/mixins/SearchPageAuthMixin.vue'
    import SupplierNameLabel from '@/components/hotels/snippets/SupplierNameLabel.vue'
    import TransferOperatedByLabel from '@/components/transfers/snippets/TransferOperatedByLabel.vue'
    import {useTransferRuntimeData} from '~/composables/transfer'
    import ProductVariantLabel from '~/components/snippets/ProductVariantLabel.vue'

    @Component({
        components: {
            ProductVariantLabel,
            TransferOperatedByLabel,
            LoadingPageLayout,
            TransferConditionsModalInfo,
            ExpiredOffersSnackbar,
            ViewOffersButton,
            TransferOfferCard,
            TransferBreadcrumbs,
            MobileInfoPageTabs,
            TransferInfo,
            TransferSearchSummary,
            TransferMap,
            ConditionsModal,
            TransferInfoModal,
            ProductGallery,
            ProductGalleryMobile,
            SupplierNameLabel,
        },
        async setup() {
            const route = useRoute()
            const {
                arrivalHotelCode,
                arrivalPlace,
                arrivalCityId,
                arrivalPlaceId,
                departureHotelCode,
                departurePlace,
                departureCityId,
                departurePlaceId,
            } = route.query
            const queryKey = new URLSearchParams({
                arrivalHotelCode,
                arrivalPlace,
                arrivalCityId,
                arrivalPlaceId,
                departureHotelCode,
                departurePlace,
                departureCityId,
                departurePlaceId,
            })
            await useAsyncData(`transfer-${queryKey}`, async () => {
                await useTransferRuntimeData(route)
                return true
            })
        },
    })
    export class TransferPage extends mixins(
        TransferPageMixin,
        TransferStoreMixin,
        SearchPageMixin,
        InfoPageMixin,
        SearchPageAuthMixin
    ) {
        get mapPoints() {
            return [transfersRuntimeStore.departurePoint, transfersRuntimeStore.arrivalPoint]
        }

        get uniqueImages() {
            return this.images.filter((v, i, a) => a.findIndex(t => t.url === v.url) === i)
        }

        get isTO1() {
            return authStore.isTO1
        }
    }

    export default TransferPage
</script>

<style scoped lang="scss">
    .disable-top {
        top: 0 !important;
    }
</style>
